import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  TrendingUp,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Loader2,
  Filter,
} from 'lucide-react'
import { format, startOfMonth, endOfMonth, addMonths, subMonths, startOfYear, endOfYear, startOfWeek, endOfWeek, addWeeks, subWeeks, getISOWeek } from 'date-fns'
import { sv } from 'date-fns/locale'
import { useOutstandingReports, useStatistics } from '../../hooks/useStatistics'
import type { OutstandingCustomerGroup, OutstandingSubcontractorGroup } from '../../types/statistics'

function formatCurrency(amount: number): string {
  return Math.round(amount).toLocaleString('sv-SE') + ' kr'
}

function formatPercent(value: number): string {
  return value.toFixed(1).replace('.', ',') + ' %'
}

export function Statistics() {
  const navigate = useNavigate()

  const [currentDate, setCurrentDate] = useState(() => new Date())
  const [viewMode, setViewMode] = useState<'week' | 'month' | 'year' | 'custom'>('month')
  const [customFrom, setCustomFrom] = useState(() => format(startOfMonth(new Date()), 'yyyy-MM-dd'))
  const [customTo, setCustomTo] = useState(() => format(endOfMonth(new Date()), 'yyyy-MM-dd'))

  const weekOptions = { weekStartsOn: 1 as const }

  const dateFrom = viewMode === 'custom'
    ? customFrom
    : viewMode === 'year'
    ? format(startOfYear(currentDate), 'yyyy-MM-dd')
    : viewMode === 'week'
    ? format(startOfWeek(currentDate, weekOptions), 'yyyy-MM-dd')
    : format(startOfMonth(currentDate), 'yyyy-MM-dd')
  const dateTo = viewMode === 'custom'
    ? customTo
    : viewMode === 'year'
    ? format(endOfYear(currentDate), 'yyyy-MM-dd')
    : viewMode === 'week'
    ? format(endOfWeek(currentDate, weekOptions), 'yyyy-MM-dd')
    : format(endOfMonth(currentDate), 'yyyy-MM-dd')

  const { data: stats, isLoading } = useStatistics(dateFrom, dateTo)
  const { data: outstanding, isLoading: outstandingLoading } = useOutstandingReports()

  const [selectedTg, setSelectedTg] = useState<string>('')
  const [expandedSubIds, setExpandedSubIds] = useState<Set<string>>(() => new Set())
  const [expandedCustomerIds, setExpandedCustomerIds] = useState<Set<string>>(() => new Set())

  const toggleSubExpanded = (id: string) => {
    setExpandedSubIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const toggleCustomerExpanded = (id: string) => {
    setExpandedCustomerIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  // Get unique TG names for the filter dropdown
  const allTgNames = useMemo(() => {
    if (!stats) return []
    const names = new Set<string>()
    for (const a of stats.assignments) {
      for (const name of a.subcontractor_names) {
        names.add(name)
      }
    }
    return Array.from(names).sort()
  }, [stats])

  // Filter assignments by selected TG — proportional share of revenue/provisions
  const filteredStats = useMemo(() => {
    if (!stats || !selectedTg) return stats
    const filtered = stats.assignments
      .filter(a => a.subcontractor_names.includes(selectedTg))
      .map(a => {
        const isAdminTg = a.admin_subcontractor_names.includes(selectedTg)
        const tgCost = a.costs_by_subcontractor[selectedTg] || 0
        const tgRevenue = a.revenue_by_subcontractor[selectedTg] || 0
        // Provision proportional to this TG's revenue vs total revenue
        const revenueShare = a.customer_revenue > 0 ? tgRevenue / a.customer_revenue : 0
        const revenue = tgRevenue
        const provision = a.provision_amount * revenueShare
        const profit = revenue - tgCost - provision
        return {
          ...a,
          customer_revenue: revenue,
          ue_revenue: isAdminTg ? 0 : revenue,
          admin_revenue: isAdminTg ? revenue : 0,
          tg_costs: isAdminTg ? 0 : tgCost,
          admin_tg_costs: isAdminTg ? tgCost : 0,
          provision_amount: provision,
          ue_provision: isAdminTg ? 0 : provision,
          admin_provision: isAdminTg ? provision : 0,
          profit,
          margin_percent: revenue > 0 ? (profit / revenue) * 100 : 0,
        }
      })
    const totalRevenue = filtered.reduce((s, a) => s + a.customer_revenue, 0)
    const totalTgCosts = filtered.reduce((s, a) => s + a.tg_costs, 0)
    const totalAdminTgCosts = filtered.reduce((s, a) => s + a.admin_tg_costs, 0)
    const totalProvisions = filtered.reduce((s, a) => s + a.provision_amount, 0)
    const totalProfit = totalRevenue - totalTgCosts - totalAdminTgCosts - totalProvisions
    return {
      ...stats,
      assignments: filtered,
      assignment_count: filtered.length,
      total_revenue: totalRevenue,
      total_tg_costs: totalTgCosts,
      total_admin_tg_costs: totalAdminTgCosts,
      total_provisions: totalProvisions,
      total_profit: totalProfit,
      average_margin: totalRevenue > 0 ? (totalProfit / totalRevenue) * 100 : 0,
    }
  }, [stats, selectedTg])

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <TrendingUp className="h-6 w-6 text-nature-700 dark:text-nature-300" />
        <h1 className="text-2xl font-bold text-nature-900 dark:text-nature-100">Statistik</h1>
      </div>

      {/* Period filter */}
      <div className="bg-[#faf9f7] dark:bg-nature-800 rounded-xl p-4 shadow-sm border border-nature-200 dark:border-nature-700">
        <div className="flex items-center justify-between mb-3">
          {viewMode !== 'custom' ? (
            <>
              <button
                onClick={() => {
                  if (viewMode === 'week') setCurrentDate(prev => subWeeks(prev, 1))
                  else if (viewMode === 'year') setCurrentDate(prev => new Date(prev.getFullYear() - 1, prev.getMonth(), 1))
                  else setCurrentDate(prev => subMonths(prev, 1))
                }}
                className="p-2 hover:bg-nature-100 dark:hover:bg-nature-700 rounded-lg transition-colors"
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <h2 className="text-lg font-semibold text-nature-900 dark:text-nature-100 capitalize">
                {viewMode === 'year'
                  ? format(currentDate, 'yyyy')
                  : viewMode === 'week'
                  ? `Vecka ${getISOWeek(currentDate)}, ${format(startOfWeek(currentDate, weekOptions), 'yyyy-MM-dd')} – ${format(endOfWeek(currentDate, weekOptions), 'yyyy-MM-dd')}`
                  : format(currentDate, 'MMMM yyyy', { locale: sv })
                }
              </h2>
              <button
                onClick={() => {
                  if (viewMode === 'week') setCurrentDate(prev => addWeeks(prev, 1))
                  else if (viewMode === 'year') setCurrentDate(prev => new Date(prev.getFullYear() + 1, prev.getMonth(), 1))
                  else setCurrentDate(prev => addMonths(prev, 1))
                }}
                className="p-2 hover:bg-nature-100 dark:hover:bg-nature-700 rounded-lg transition-colors"
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </>
          ) : (
            <div className="flex items-center gap-2 w-full justify-center">
              <input
                type="date"
                value={customFrom}
                onChange={e => setCustomFrom(e.target.value)}
                className="text-sm bg-nature-100 dark:bg-nature-700 text-nature-900 dark:text-nature-100 rounded-lg px-3 py-1.5 border border-nature-200 dark:border-nature-600"
              />
              <span className="text-nature-500 dark:text-nature-400">–</span>
              <input
                type="date"
                value={customTo}
                onChange={e => setCustomTo(e.target.value)}
                className="text-sm bg-nature-100 dark:bg-nature-700 text-nature-900 dark:text-nature-100 rounded-lg px-3 py-1.5 border border-nature-200 dark:border-nature-600"
              />
            </div>
          )}
        </div>
        <div className="flex flex-wrap gap-2 justify-center">
          <button
            onClick={() => { setViewMode('week'); setCurrentDate(new Date()) }}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
              viewMode === 'week' && getISOWeek(currentDate) === getISOWeek(new Date()) && currentDate.getFullYear() === new Date().getFullYear()
                ? 'bg-nature-700 text-white dark:bg-nature-600'
                : 'bg-nature-100 text-nature-700 hover:bg-nature-200 dark:bg-nature-700 dark:text-nature-300 dark:hover:bg-nature-600'
            }`}
          >
            Denna vecka
          </button>
          <button
            onClick={() => { setViewMode('month'); setCurrentDate(new Date()) }}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
              viewMode === 'month' && format(currentDate, 'yyyy-MM') === format(new Date(), 'yyyy-MM')
                ? 'bg-nature-700 text-white dark:bg-nature-600'
                : 'bg-nature-100 text-nature-700 hover:bg-nature-200 dark:bg-nature-700 dark:text-nature-300 dark:hover:bg-nature-600'
            }`}
          >
            Denna månad
          </button>
          <button
            onClick={() => { setViewMode('month'); setCurrentDate(subMonths(new Date(), 1)) }}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
              viewMode === 'month' && format(currentDate, 'yyyy-MM') === format(subMonths(new Date(), 1), 'yyyy-MM')
                ? 'bg-nature-700 text-white dark:bg-nature-600'
                : 'bg-nature-100 text-nature-700 hover:bg-nature-200 dark:bg-nature-700 dark:text-nature-300 dark:hover:bg-nature-600'
            }`}
          >
            Föregående månad
          </button>
          <button
            onClick={() => { setViewMode('year'); setCurrentDate(new Date()) }}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
              viewMode === 'year'
                ? 'bg-nature-700 text-white dark:bg-nature-600'
                : 'bg-nature-100 text-nature-700 hover:bg-nature-200 dark:bg-nature-700 dark:text-nature-300 dark:hover:bg-nature-600'
            }`}
          >
            I år
          </button>
          <button
            onClick={() => setViewMode('custom')}
            className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
              viewMode === 'custom'
                ? 'bg-nature-700 text-white dark:bg-nature-600'
                : 'bg-nature-100 text-nature-700 hover:bg-nature-200 dark:bg-nature-700 dark:text-nature-300 dark:hover:bg-nature-600'
            }`}
          >
            Anpassad
          </button>
        </div>
        {allTgNames.length > 0 && (
          <div className="flex items-center gap-2 mt-3 justify-center">
            <Filter className="h-4 w-4 text-nature-500 dark:text-nature-400" />
            <select
              value={selectedTg}
              onChange={e => setSelectedTg(e.target.value)}
              className="text-sm bg-nature-100 dark:bg-nature-700 text-nature-900 dark:text-nature-100 rounded-lg px-3 py-1.5 border border-nature-200 dark:border-nature-600"
            >
              <option value="">Alla trädgårdsmästare</option>
              {allTgNames.map(name => (
                <option key={name} value={name}>{name}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="flex items-center justify-center h-32">
          <Loader2 className="h-8 w-8 animate-spin text-nature-600" />
        </div>
      )}

      {/* Content */}
      {!isLoading && filteredStats && (() => {
        const s = filteredStats
        // For mixed assignments (both UE and admin costs), split revenue and provisions
        // proportionally based on cost share so that UE + Egenregi = Totalt
        // UE: assignments that have at least one non-admin subcontractor
        // UE: use actual revenue/provision from UE subcontractors' reported work
        const ueData = s.assignments
          .filter(a => a.subcontractor_names.some(n => !a.admin_subcontractor_names.includes(n)))
          .map(a => {
            const revenue = a.ue_revenue
            const provision = a.ue_provision
            const profit = revenue - a.tg_costs - provision
            const margin = revenue > 0 ? (profit / revenue) * 100 : 0
            return { ...a, revenue, costs: a.tg_costs, provision, profit, margin }
          })

        // Egenregi: use actual revenue/provision from admin employees' reported work
        const adminData = s.assignments
          .filter(a => a.admin_subcontractor_names.length > 0)
          .map(a => {
            const revenue = a.admin_revenue
            const provision = a.admin_provision
            const profit = revenue - a.admin_tg_costs - provision
            const margin = revenue > 0 ? (profit / revenue) * 100 : 0
            return { ...a, revenue, costs: a.admin_tg_costs, provision, profit, margin }
          })

        const ueRevenue = ueData.reduce((s, a) => s + a.revenue, 0)
        const ueCosts = ueData.reduce((s, a) => s + a.costs, 0)
        const ueProvisions = ueData.reduce((s, a) => s + a.provision, 0)
        const ueProfit = ueRevenue - ueCosts - ueProvisions
        const ueTotals = {
          revenue: ueRevenue, costs: ueCosts, provisions: ueProvisions,
          profit: ueProfit, margin: ueRevenue > 0 ? (ueProfit / ueRevenue) * 100 : 0,
        }

        const adminRevenue = adminData.reduce((s, a) => s + a.revenue, 0)
        const adminCosts = adminData.reduce((s, a) => s + a.costs, 0)
        const adminProvisions = adminData.reduce((s, a) => s + a.provision, 0)
        const adminProfit = adminRevenue - adminCosts - adminProvisions
        const adminTotals = {
          revenue: adminRevenue, costs: adminCosts, provisions: adminProvisions,
          profit: adminProfit, margin: adminRevenue > 0 ? (adminProfit / adminRevenue) * 100 : 0,
        }

        const combinedCosts = s.total_tg_costs + s.total_admin_tg_costs
        const combinedProfit = s.total_revenue - combinedCosts - s.total_provisions
        const combinedMargin = s.total_revenue > 0 ? (combinedProfit / s.total_revenue) * 100 : 0

        return (
        <>
          {/* UE Section */}
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-bold text-nature-900 dark:text-nature-100">Underentreprenörer</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
            <SummaryCard
              label="Intäkter"
              value={formatCurrency(ueTotals.revenue)}
              colorClass="text-blue-600 dark:text-blue-400"
              bgClass="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800"
            />
            <SummaryCard
              label="UE-kostnader"
              value={formatCurrency(ueTotals.costs)}
              colorClass="text-orange-600 dark:text-orange-400"
              bgClass="bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800"
            />
            <SummaryCard
              label="Provisioner"
              value={formatCurrency(ueTotals.provisions)}
              colorClass="text-amber-600 dark:text-amber-400"
              bgClass="bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800"
            />
            <SummaryCard
              label="Resultat"
              value={formatCurrency(ueTotals.profit)}
              colorClass={ueTotals.profit >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}
              bgClass={ueTotals.profit >= 0
                ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
              }
            />
            <SummaryCard
              label="Marginal"
              value={formatPercent(ueTotals.margin)}
              colorClass={ueTotals.margin >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}
              bgClass={ueTotals.margin >= 0
                ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
              }
            />
          </div>

          {/* UE Assignment table */}
          <div className="bg-[#faf9f7] dark:bg-nature-800 rounded-xl shadow-sm border border-nature-200 dark:border-nature-700">
            {ueData.length === 0 ? (
              <div className="p-8 text-center text-nature-500 dark:text-nature-400">
                Inga uppdrag med underentreprenörer för vald period
              </div>
            ) : (
              <>
                {/* Mobile view */}
                <div className="lg:hidden divide-y divide-nature-200 dark:divide-nature-700">
                  {ueData.map(a => (
                    <div
                      key={a.assignment_id}
                      onClick={() => navigate(`/admin/assignments/${a.assignment_id}`)}
                      className="p-4 hover:bg-nature-50 dark:hover:bg-nature-700/50 transition-colors cursor-pointer"
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-nature-900 dark:text-nature-100 truncate">
                          {a.assignment_number ? `#${a.assignment_number} ` : ''}{a.title}
                        </span>
                        <span className="text-xs text-nature-500 dark:text-nature-400 ml-2 flex-shrink-0">
                          {a.start_date}
                        </span>
                      </div>
                      <div className="text-xs text-nature-500 dark:text-nature-400">{a.customer_name}</div>
                      {a.subcontractor_names.length > 0 && (
                        <div className="text-xs text-nature-400 dark:text-nature-500 mb-2">TG: {a.subcontractor_names.join(', ')}</div>
                      )}
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div>
                          <span className="text-nature-500 dark:text-nature-400">Intäkter: </span>
                          <span className="font-medium text-nature-900 dark:text-nature-100">{formatCurrency(a.revenue)}</span>
                        </div>
                        <div>
                          <span className="text-nature-500 dark:text-nature-400">UE-kost: </span>
                          <span className="font-medium text-nature-900 dark:text-nature-100">{formatCurrency(a.costs)}</span>
                        </div>
                        <div>
                          <span className="text-nature-500 dark:text-nature-400">Provision: </span>
                          <span className="font-medium text-nature-900 dark:text-nature-100">{formatCurrency(a.provision)}</span>
                        </div>
                        <div>
                          <span className="text-nature-500 dark:text-nature-400">Resultat: </span>
                          <span className={`font-medium ${a.profit >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                            {formatCurrency(a.profit)}
                          </span>
                        </div>
                        <div>
                          <span className="text-nature-500 dark:text-nature-400">Marginal: </span>
                          <span className={`font-medium ${a.margin >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                            {formatPercent(a.margin)}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Desktop view */}
                <div className="hidden lg:block overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-nature-200 dark:border-nature-700">
                        <th className="text-left px-4 py-3 text-xs font-medium text-nature-500 dark:text-nature-400 uppercase">Uppdrag</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-nature-500 dark:text-nature-400 uppercase">Datum</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-nature-500 dark:text-nature-400 uppercase">Kund</th>
                        <th className="text-right px-4 py-3 text-xs font-medium text-nature-500 dark:text-nature-400 uppercase">Intäkter</th>
                        <th className="text-right px-4 py-3 text-xs font-medium text-nature-500 dark:text-nature-400 uppercase">UE-kostnader</th>
                        <th className="text-right px-4 py-3 text-xs font-medium text-nature-500 dark:text-nature-400 uppercase">Provisioner</th>
                        <th className="text-right px-4 py-3 text-xs font-medium text-nature-500 dark:text-nature-400 uppercase">Resultat</th>
                        <th className="text-right px-4 py-3 text-xs font-medium text-nature-500 dark:text-nature-400 uppercase">Marginal</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-nature-100 dark:divide-nature-700">
                      {ueData.map(a => (
                        <tr
                          key={a.assignment_id}
                          onClick={() => navigate(`/admin/assignments/${a.assignment_id}`)}
                          className="hover:bg-nature-50 dark:hover:bg-nature-700/50 transition-colors cursor-pointer"
                        >
                          <td className="px-4 py-3 text-sm text-nature-900 dark:text-nature-100">
                            <div>{a.assignment_number ? `#${a.assignment_number} ` : ''}{a.title}</div>
                            {a.subcontractor_names.length > 0 && (
                              <div className="text-xs text-nature-400 dark:text-nature-500">{a.subcontractor_names.join(', ')}</div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-nature-600 dark:text-nature-300">{a.start_date}</td>
                          <td className="px-4 py-3 text-sm text-nature-600 dark:text-nature-300 max-w-[200px] truncate">{a.customer_name}</td>
                          <td className="px-4 py-3 text-sm text-nature-900 dark:text-nature-100 text-right">{formatCurrency(a.revenue)}</td>
                          <td className="px-4 py-3 text-sm text-nature-600 dark:text-nature-300 text-right">{formatCurrency(a.costs)}</td>
                          <td className="px-4 py-3 text-sm text-nature-600 dark:text-nature-300 text-right">{formatCurrency(a.provision)}</td>
                          <td className={`px-4 py-3 text-sm font-medium text-right ${a.profit >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                            {formatCurrency(a.profit)}
                          </td>
                          <td className={`px-4 py-3 text-sm font-medium text-right ${a.margin >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                            {formatPercent(a.margin)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-nature-300 dark:border-nature-600 bg-nature-50 dark:bg-nature-700/30">
                        <td className="px-4 py-3 text-sm font-semibold text-nature-900 dark:text-nature-100" colSpan={3}>
                          Totalt ({ueData.length} uppdrag)
                        </td>
                        <td className="px-4 py-3 text-sm font-semibold text-nature-900 dark:text-nature-100 text-right">
                          {formatCurrency(ueTotals.revenue)}
                        </td>
                        <td className="px-4 py-3 text-sm font-semibold text-nature-600 dark:text-nature-300 text-right">
                          {formatCurrency(ueTotals.costs)}
                        </td>
                        <td className="px-4 py-3 text-sm font-semibold text-nature-600 dark:text-nature-300 text-right">
                          {formatCurrency(ueTotals.provisions)}
                        </td>
                        <td className={`px-4 py-3 text-sm font-semibold text-right ${ueTotals.profit >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                          {formatCurrency(ueTotals.profit)}
                        </td>
                        <td className={`px-4 py-3 text-sm font-semibold text-right ${ueTotals.margin >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                          {formatPercent(ueTotals.margin)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </>
            )}
          </div>

          {/* Egenregi section */}
          {adminData.length > 0 && (
            <>
              <div className="flex items-center gap-3 mt-4">
                <h2 className="text-lg font-bold text-nature-900 dark:text-nature-100">Egenregi (anställda)</h2>
                <span className="text-xs text-nature-500 dark:text-nature-400">Kostnad utifrån prislista</span>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
                <SummaryCard
                  label="Intäkter"
                  value={formatCurrency(adminTotals.revenue)}
                  colorClass="text-blue-600 dark:text-blue-400"
                  bgClass="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800"
                />
                <SummaryCard
                  label="Egenregi-kostnader"
                  value={formatCurrency(adminTotals.costs)}
                  colorClass="text-orange-600 dark:text-orange-400"
                  bgClass="bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800"
                />
                <SummaryCard
                  label="Provisioner"
                  value={formatCurrency(adminTotals.provisions)}
                  colorClass="text-amber-600 dark:text-amber-400"
                  bgClass="bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800"
                />
                <SummaryCard
                  label="Resultat"
                  value={formatCurrency(adminTotals.profit)}
                  colorClass={adminTotals.profit >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}
                  bgClass={adminTotals.profit >= 0
                    ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                    : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                  }
                />
                <SummaryCard
                  label="Marginal"
                  value={formatPercent(adminTotals.margin)}
                  colorClass={adminTotals.margin >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}
                  bgClass={adminTotals.margin >= 0
                    ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                    : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                  }
                />
              </div>

              <div className="bg-[#faf9f7] dark:bg-nature-800 rounded-xl shadow-sm border border-nature-200 dark:border-nature-700">
                {/* Mobile */}
                <div className="lg:hidden divide-y divide-nature-200 dark:divide-nature-700">
                  {adminData.map(a => (
                    <div
                      key={`admin-${a.assignment_id}`}
                      onClick={() => navigate(`/admin/assignments/${a.assignment_id}`)}
                      className="p-4 hover:bg-nature-50 dark:hover:bg-nature-700/50 transition-colors cursor-pointer"
                    >
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-sm font-medium text-nature-900 dark:text-nature-100 truncate">
                          {a.assignment_number ? `#${a.assignment_number} ` : ''}{a.title}
                        </span>
                        <span className="text-xs text-nature-500 dark:text-nature-400 ml-2 flex-shrink-0">
                          {a.start_date}
                        </span>
                      </div>
                      <div className="text-xs text-nature-500 dark:text-nature-400">{a.customer_name}</div>
                      {a.subcontractor_names.length > 0 && (
                        <div className="text-xs text-nature-400 dark:text-nature-500 mb-2">TG: {a.subcontractor_names.join(', ')}</div>
                      )}
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div>
                          <span className="text-nature-500 dark:text-nature-400">Intäkter: </span>
                          <span className="font-medium text-nature-900 dark:text-nature-100">{formatCurrency(a.revenue)}</span>
                        </div>
                        <div>
                          <span className="text-nature-500 dark:text-nature-400">Egenregi-kost: </span>
                          <span className="font-medium text-nature-900 dark:text-nature-100">{formatCurrency(a.costs)}</span>
                        </div>
                        <div>
                          <span className="text-nature-500 dark:text-nature-400">Provision: </span>
                          <span className="font-medium text-nature-900 dark:text-nature-100">{formatCurrency(a.provision)}</span>
                        </div>
                        <div>
                          <span className="text-nature-500 dark:text-nature-400">Resultat: </span>
                          <span className={`font-medium ${a.profit >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                            {formatCurrency(a.profit)}
                          </span>
                        </div>
                        <div>
                          <span className="text-nature-500 dark:text-nature-400">Marginal: </span>
                          <span className={`font-medium ${a.margin >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                            {formatPercent(a.margin)}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Desktop */}
                <div className="hidden lg:block overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-nature-200 dark:border-nature-700">
                        <th className="text-left px-4 py-3 text-xs font-medium text-nature-500 dark:text-nature-400 uppercase">Uppdrag</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-nature-500 dark:text-nature-400 uppercase">Datum</th>
                        <th className="text-left px-4 py-3 text-xs font-medium text-nature-500 dark:text-nature-400 uppercase">Kund</th>
                        <th className="text-right px-4 py-3 text-xs font-medium text-nature-500 dark:text-nature-400 uppercase">Intäkter</th>
                        <th className="text-right px-4 py-3 text-xs font-medium text-nature-500 dark:text-nature-400 uppercase">Egenregi-kost</th>
                        <th className="text-right px-4 py-3 text-xs font-medium text-nature-500 dark:text-nature-400 uppercase">Provisioner</th>
                        <th className="text-right px-4 py-3 text-xs font-medium text-nature-500 dark:text-nature-400 uppercase">Resultat</th>
                        <th className="text-right px-4 py-3 text-xs font-medium text-nature-500 dark:text-nature-400 uppercase">Marginal</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-nature-200 dark:divide-nature-700">
                      {adminData.map(a => (
                        <tr
                          key={`admin-${a.assignment_id}`}
                          onClick={() => navigate(`/admin/assignments/${a.assignment_id}`)}
                          className="hover:bg-nature-50 dark:hover:bg-nature-700/50 transition-colors cursor-pointer"
                        >
                          <td className="px-4 py-3 text-sm text-nature-900 dark:text-nature-100">
                            <div>{a.assignment_number ? `#${a.assignment_number} ` : ''}{a.title}</div>
                            {a.subcontractor_names.length > 0 && (
                              <div className="text-xs text-nature-400 dark:text-nature-500">{a.subcontractor_names.join(', ')}</div>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-nature-600 dark:text-nature-300">{a.start_date}</td>
                          <td className="px-4 py-3 text-sm text-nature-600 dark:text-nature-300 max-w-[200px] truncate">{a.customer_name}</td>
                          <td className="px-4 py-3 text-sm text-nature-900 dark:text-nature-100 text-right">{formatCurrency(a.revenue)}</td>
                          <td className="px-4 py-3 text-sm text-nature-600 dark:text-nature-300 text-right">{formatCurrency(a.costs)}</td>
                          <td className="px-4 py-3 text-sm text-nature-600 dark:text-nature-300 text-right">{formatCurrency(a.provision)}</td>
                          <td className={`px-4 py-3 text-sm font-medium text-right ${a.profit >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                            {formatCurrency(a.profit)}
                          </td>
                          <td className={`px-4 py-3 text-sm font-medium text-right ${a.margin >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                            {formatPercent(a.margin)}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2 border-nature-300 dark:border-nature-600 bg-nature-50 dark:bg-nature-700/30">
                        <td className="px-4 py-3 text-sm font-semibold text-nature-900 dark:text-nature-100" colSpan={3}>
                          Totalt ({adminData.length} uppdrag)
                        </td>
                        <td className="px-4 py-3 text-sm font-semibold text-nature-900 dark:text-nature-100 text-right">
                          {formatCurrency(adminTotals.revenue)}
                        </td>
                        <td className="px-4 py-3 text-sm font-semibold text-nature-600 dark:text-nature-300 text-right">
                          {formatCurrency(adminTotals.costs)}
                        </td>
                        <td className="px-4 py-3 text-sm font-semibold text-nature-600 dark:text-nature-300 text-right">
                          {formatCurrency(adminTotals.provisions)}
                        </td>
                        <td className={`px-4 py-3 text-sm font-semibold text-right ${adminTotals.profit >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                          {formatCurrency(adminTotals.profit)}
                        </td>
                        <td className={`px-4 py-3 text-sm font-semibold text-right ${adminTotals.margin >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                          {formatPercent(adminTotals.margin)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            </>
          )}

          {/* Combined total */}
          {s.assignments.length > 0 && (
            <>
              <div className="flex items-center gap-3 mt-4">
                <h2 className="text-lg font-bold text-nature-900 dark:text-nature-100">Totalt</h2>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
                <SummaryCard
                  label="Intäkter"
                  value={formatCurrency(s.total_revenue)}
                  colorClass="text-blue-600 dark:text-blue-400"
                  bgClass="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800"
                />
                <SummaryCard
                  label="Kostnader"
                  value={formatCurrency(combinedCosts)}
                  colorClass="text-orange-600 dark:text-orange-400"
                  bgClass="bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800"
                />
                <SummaryCard
                  label="Provisioner"
                  value={formatCurrency(s.total_provisions)}
                  colorClass="text-amber-600 dark:text-amber-400"
                  bgClass="bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800"
                />
                <SummaryCard
                  label="Resultat"
                  value={formatCurrency(combinedProfit)}
                  colorClass={combinedProfit >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}
                  bgClass={combinedProfit >= 0
                    ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                    : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                  }
                />
                <SummaryCard
                  label="Marginal"
                  value={formatPercent(combinedMargin)}
                  colorClass={combinedMargin >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}
                  bgClass={combinedMargin >= 0
                    ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800'
                    : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'
                  }
                />
              </div>
            </>
          )}
        </>
        )
      })()}

      {/* Outstanding section — independent of date filter */}
      <div className="pt-4 border-t border-nature-200 dark:border-nature-700">
        <div className="flex items-baseline gap-3 mb-1">
          <h2 className="text-lg font-bold text-nature-900 dark:text-nature-100">Utestående</h2>
          <span className="text-xs text-nature-500 dark:text-nature-400">
            Visar allt rapporterat och ej fakturerat — oberoende av perioden ovan
          </span>
        </div>

        {outstandingLoading && (
          <div className="flex items-center justify-center h-24">
            <Loader2 className="h-6 w-6 animate-spin text-nature-600" />
          </div>
        )}

        {!outstandingLoading && outstanding && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3">
              <OutstandingSummaryCard
                label="Att betala till underentreprenörer"
                amount={outstanding.toPaySubcontractors.total}
                groupCount={outstanding.toPaySubcontractors.groups.length}
                groupNoun="UE"
                assignmentCount={outstanding.toPaySubcontractors.assignment_count}
                colorClass="text-orange-600 dark:text-orange-400"
                bgClass="bg-orange-50 dark:bg-orange-900/20 border-orange-200 dark:border-orange-800"
              />
              <OutstandingSummaryCard
                label="Att fakturera ut till kund"
                amount={outstanding.toInvoiceCustomers.total}
                groupCount={outstanding.toInvoiceCustomers.groups.length}
                groupNoun="kunder"
                assignmentCount={outstanding.toInvoiceCustomers.assignment_count}
                colorClass="text-blue-600 dark:text-blue-400"
                bgClass="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800"
              />
            </div>

            {/* Att betala UE — grouped table */}
            <div className="mt-6">
              <h3 className="text-sm font-semibold text-nature-700 dark:text-nature-200 mb-2">
                Att betala till underentreprenörer
              </h3>
              <OutstandingSubcontractorsTable
                groups={outstanding.toPaySubcontractors.groups}
                expandedIds={expandedSubIds}
                onToggle={toggleSubExpanded}
                total={outstanding.toPaySubcontractors.total}
                onAssignmentClick={(id) => navigate(`/admin/assignments/${id}`)}
              />
            </div>

            {/* Att fakturera kund — grouped table */}
            <div className="mt-6">
              <h3 className="text-sm font-semibold text-nature-700 dark:text-nature-200 mb-2">
                Att fakturera ut till kund
              </h3>
              <OutstandingCustomersTable
                groups={outstanding.toInvoiceCustomers.groups}
                expandedIds={expandedCustomerIds}
                onToggle={toggleCustomerExpanded}
                total={outstanding.toInvoiceCustomers.total}
                onAssignmentClick={(id) => navigate(`/admin/assignments/${id}`)}
              />
            </div>
          </>
        )}
      </div>
    </div>
  )
}

function OutstandingSummaryCard({ label, amount, groupCount, groupNoun, assignmentCount, colorClass, bgClass }: {
  label: string
  amount: number
  groupCount: number
  groupNoun: string
  assignmentCount: number
  colorClass: string
  bgClass: string
}) {
  return (
    <div className={`rounded-xl p-4 shadow-sm border ${bgClass}`}>
      <div className="text-sm text-nature-500 dark:text-nature-400">{label}</div>
      <div className={`text-2xl font-bold ${colorClass}`}>{formatCurrency(amount)}</div>
      <div className="text-xs text-nature-500 dark:text-nature-400 mt-1">
        {groupCount} {groupNoun}, {assignmentCount} uppdrag
      </div>
    </div>
  )
}

function OutstandingSubcontractorsTable({ groups, expandedIds, onToggle, total, onAssignmentClick }: {
  groups: OutstandingSubcontractorGroup[]
  expandedIds: Set<string>
  onToggle: (id: string) => void
  total: number
  onAssignmentClick: (assignmentId: string) => void
}) {
  if (groups.length === 0) {
    return (
      <div className="bg-[#faf9f7] dark:bg-nature-800 rounded-xl shadow-sm border border-nature-200 dark:border-nature-700 p-6 text-center text-sm text-nature-500 dark:text-nature-400">
        Inga utestående poster
      </div>
    )
  }

  return (
    <div className="bg-[#faf9f7] dark:bg-nature-800 rounded-xl shadow-sm border border-nature-200 dark:border-nature-700 overflow-hidden">
      <div className="divide-y divide-nature-200 dark:divide-nature-700">
        {groups.map(group => {
          const expanded = expandedIds.has(group.subcontractor_id)
          return (
            <div key={group.subcontractor_id}>
              <button
                type="button"
                onClick={() => onToggle(group.subcontractor_id)}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-nature-50 dark:hover:bg-nature-700/50 transition-colors text-left"
              >
                <ChevronDown
                  className={`h-4 w-4 text-nature-500 dark:text-nature-400 flex-shrink-0 transition-transform ${expanded ? '' : '-rotate-90'}`}
                />
                <span className="flex-1 text-sm font-medium text-nature-900 dark:text-nature-100 truncate">
                  {group.subcontractor_name}
                </span>
                <span className="text-xs text-nature-500 dark:text-nature-400 flex-shrink-0">
                  {group.assignment_count} uppdrag
                </span>
                <span className="text-sm font-semibold text-orange-600 dark:text-orange-400 flex-shrink-0 w-32 text-right">
                  {formatCurrency(group.total_excl_vat)}
                </span>
              </button>
              {expanded && (
                <div className="bg-nature-50/60 dark:bg-nature-900/30 px-4 py-2 divide-y divide-nature-100 dark:divide-nature-800">
                  {group.assignments.map(a => (
                    <button
                      type="button"
                      key={a.assignment_id}
                      onClick={() => onAssignmentClick(a.assignment_id)}
                      className="w-full flex items-center gap-3 py-2 text-left hover:bg-nature-100 dark:hover:bg-nature-700/40 rounded transition-colors px-2"
                    >
                      <span className="text-xs text-nature-500 dark:text-nature-400 flex-shrink-0 w-24">
                        {a.start_date}
                      </span>
                      <span className="flex-1 text-sm text-nature-800 dark:text-nature-200 truncate">
                        {a.assignment_number ? `#${a.assignment_number} ` : ''}{a.title}
                        <span className="text-xs text-nature-500 dark:text-nature-400"> · {a.customer_name}</span>
                      </span>
                      <span className="text-sm text-nature-700 dark:text-nature-300 flex-shrink-0 w-28 text-right">
                        {formatCurrency(a.amount_excl_vat)}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )
        })}
      </div>
      <div className="border-t-2 border-nature-300 dark:border-nature-600 bg-nature-50 dark:bg-nature-700/30 px-4 py-3 flex items-center">
        <span className="flex-1 text-sm font-semibold text-nature-900 dark:text-nature-100">
          Totalt ({groups.length} UE)
        </span>
        <span className="text-sm font-semibold text-orange-600 dark:text-orange-400 w-32 text-right">
          {formatCurrency(total)}
        </span>
      </div>
    </div>
  )
}

function OutstandingCustomersTable({ groups, expandedIds, onToggle, total, onAssignmentClick }: {
  groups: OutstandingCustomerGroup[]
  expandedIds: Set<string>
  onToggle: (id: string) => void
  total: number
  onAssignmentClick: (assignmentId: string) => void
}) {
  if (groups.length === 0) {
    return (
      <div className="bg-[#faf9f7] dark:bg-nature-800 rounded-xl shadow-sm border border-nature-200 dark:border-nature-700 p-6 text-center text-sm text-nature-500 dark:text-nature-400">
        Inga utestående poster
      </div>
    )
  }

  return (
    <div className="bg-[#faf9f7] dark:bg-nature-800 rounded-xl shadow-sm border border-nature-200 dark:border-nature-700 overflow-hidden">
      <div className="divide-y divide-nature-200 dark:divide-nature-700">
        {groups.map(group => {
          const expanded = expandedIds.has(group.customer_id)
          return (
            <div key={group.customer_id}>
              <button
                type="button"
                onClick={() => onToggle(group.customer_id)}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-nature-50 dark:hover:bg-nature-700/50 transition-colors text-left"
              >
                <ChevronDown
                  className={`h-4 w-4 text-nature-500 dark:text-nature-400 flex-shrink-0 transition-transform ${expanded ? '' : '-rotate-90'}`}
                />
                <span className="flex-1 text-sm font-medium text-nature-900 dark:text-nature-100 truncate">
                  {group.customer_name}
                </span>
                <span className="text-xs text-nature-500 dark:text-nature-400 flex-shrink-0">
                  {group.assignment_count} uppdrag
                </span>
                <span className="text-sm font-semibold text-blue-600 dark:text-blue-400 flex-shrink-0 w-32 text-right">
                  {formatCurrency(group.total_excl_vat)}
                </span>
              </button>
              {expanded && (
                <div className="bg-nature-50/60 dark:bg-nature-900/30 px-4 py-2 divide-y divide-nature-100 dark:divide-nature-800">
                  {group.assignments.map(a => (
                    <button
                      type="button"
                      key={a.assignment_id}
                      onClick={() => onAssignmentClick(a.assignment_id)}
                      className="w-full flex items-center gap-3 py-2 text-left hover:bg-nature-100 dark:hover:bg-nature-700/40 rounded transition-colors px-2"
                    >
                      <span className="text-xs text-nature-500 dark:text-nature-400 flex-shrink-0 w-24">
                        {a.start_date}
                      </span>
                      <span className="flex-1 text-sm text-nature-800 dark:text-nature-200 truncate">
                        {a.assignment_number ? `#${a.assignment_number} ` : ''}{a.title}
                      </span>
                      <span className="text-sm text-nature-700 dark:text-nature-300 flex-shrink-0 w-28 text-right">
                        {formatCurrency(a.amount_excl_vat)}
                      </span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )
        })}
      </div>
      <div className="border-t-2 border-nature-300 dark:border-nature-600 bg-nature-50 dark:bg-nature-700/30 px-4 py-3 flex items-center">
        <span className="flex-1 text-sm font-semibold text-nature-900 dark:text-nature-100">
          Totalt ({groups.length} kunder)
        </span>
        <span className="text-sm font-semibold text-blue-600 dark:text-blue-400 w-32 text-right">
          {formatCurrency(total)}
        </span>
      </div>
    </div>
  )
}

function SummaryCard({ label, value, colorClass, bgClass }: {
  label: string
  value: string
  colorClass: string
  bgClass: string
}) {
  return (
    <div className={`rounded-xl p-4 shadow-sm border ${bgClass}`}>
      <div className="text-sm text-nature-500 dark:text-nature-400">{label}</div>
      <div className={`text-2xl font-bold ${colorClass}`}>{value}</div>
    </div>
  )
}
